import React, { useEffect, useState } from "react";
import { updateFreightActiveUserNotificationPrefs } from "../../utils/api";
import { getRowNotify, writeRowNotify } from "../../utils/freightRowNotifyStorage";
import { showError } from "../../utils/toast";

function toBool(v) {
  if (v === 1 || v === "1" || v === true) return true;
  if (v === 0 || v === "0" || v === false) return false;
  return true;
}

function hasPref(v) {
  return v !== undefined && v !== null && v !== "";
}

/**
 * WhatsApp / app notification toggles.
 * When `syncWithServer` (active users): POST `updateNotificationPrefs`; initial values from row when present.
 * Otherwise: localStorage only (new / inactive lists).
 */
const FreightRowNotifyToggles = ({ row, syncWithServer = false }) => {
  const rowId = row?.id;
  const [wa, setWa] = useState(true);
  const [app, setApp] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (rowId == null) return;
    const ls = getRowNotify(rowId);
    const waFromRow = row?.whatsapp_notification;
    const appFromRow = row?.app_notification;
    setWa(hasPref(waFromRow) ? toBool(waFromRow) : ls.wa);
    setApp(hasPref(appFromRow) ? toBool(appFromRow) : ls.app);
  }, [rowId, row?.whatsapp_notification, row?.app_notification]);

  if (rowId == null) return <span className="text-gray-300">—</span>;

  const base =
    "inline-flex items-center justify-center w-8 h-8 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#064E8D]/40 disabled:opacity-40 disabled:cursor-not-allowed";

  const persistLocal = (nextWa, nextApp) => {
    writeRowNotify(rowId, { wa: nextWa, app: nextApp });
  };

  const syncToServer = async (nextWa, nextApp, revert) => {
    setBusy(true);
    try {
      const res = await updateFreightActiveUserNotificationPrefs({
        user_id: rowId,
        whatsapp_notification: nextWa ? 1 : 0,
        app_notification: nextApp ? 1 : 0
      });
      const d = res?.data;
      if (d) {
        const nw = hasPref(d.whatsapp_notification) ? toBool(d.whatsapp_notification) : nextWa;
        const na = hasPref(d.app_notification) ? toBool(d.app_notification) : nextApp;
        setWa(nw);
        setApp(na);
        persistLocal(nw, na);
      } else {
        persistLocal(nextWa, nextApp);
      }
    } catch (e) {
      showError(e.message || "Failed to update notification preferences");
      revert();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center gap-0.5">
      <button
        type="button"
        disabled={busy}
        className={`${base} ${wa ? "text-green-600 hover:bg-green-50" : "text-gray-300 hover:bg-gray-100 hover:text-gray-500"}`}
        title={wa ? "WhatsApp alerts on (click to turn off)" : "WhatsApp alerts off (click to turn on)"}
        aria-pressed={wa}
        onClick={() => {
          const prevWa = wa;
          const prevApp = app;
          const nextWa = !wa;
          setWa(nextWa);
          if (!syncWithServer) {
            persistLocal(nextWa, app);
            return;
          }
          syncToServer(nextWa, app, () => {
            setWa(prevWa);
            setApp(prevApp);
          });
        }}
      >
        <i className="fab fa-whatsapp text-lg leading-none" aria-hidden />
      </button>
      <button
        type="button"
        disabled={busy}
        className={`${base} ${app ? "text-[#064E8D] hover:bg-blue-50" : "text-gray-300 hover:bg-gray-100 hover:text-gray-500"}`}
        title={app ? "App notifications on (click to turn off)" : "App notifications off (click to turn on)"}
        aria-pressed={app}
        onClick={() => {
          const prevWa = wa;
          const prevApp = app;
          const nextApp = !app;
          setApp(nextApp);
          if (!syncWithServer) {
            persistLocal(wa, nextApp);
            return;
          }
          syncToServer(wa, nextApp, () => {
            setWa(prevWa);
            setApp(prevApp);
          });
        }}
      >
        <i className="fas fa-bell text-sm leading-none" aria-hidden />
      </button>
    </div>
  );
};

export default FreightRowNotifyToggles;
